/**
 * Authentication utilities for React app
 * Provides token management and authenticated fetch
 */

import { API_GATEWAY_URL } from './config';

const DEV_AUTH_COOKIE = 'devAuth';
const ACCESS_TOKEN_COOKIE = 'accessToken';
const BILLING_LOGIN_TOKEN_KEY = 'billingLoginGrantToken';

function bytesToHex(bytes) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(value) {
  const encoded = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return bytesToHex(new Uint8Array(digest));
}

export async function prepareBillingLoginGrant() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const token = bytesToHex(bytes);
  sessionStorage.setItem(BILLING_LOGIN_TOKEN_KEY, token);
  const tokenHash = await sha256Hex(token);
  return tokenHash;
}

export function getBillingLoginToken() {
  return sessionStorage.getItem(BILLING_LOGIN_TOKEN_KEY);
}

export function clearBillingLoginToken() {
  sessionStorage.removeItem(BILLING_LOGIN_TOKEN_KEY);
}

/**
 * Set access token cookie for Lambda@Edge bundle routing
 * This cookie is NOT HttpOnly so the edge function can read it for JWT verification.
 * The cookie is set with Secure flag since both dev (HTTPS via mkcert) and prod use HTTPS.
 */
export function setAccessTokenCookie(accessToken) {
  if (!accessToken) return;

  // Decode token to get expiration time for cookie max-age
  const payload = decodeJWT(accessToken);
  let maxAge = 900; // Default 15 minutes (typical access token lifetime)

  if (payload && payload.exp) {
    // Calculate seconds until expiration
    const now = Math.floor(Date.now() / 1000);
    maxAge = Math.max(0, payload.exp - now);
  }

  // Set cookie with appropriate flags
  // - NOT HttpOnly: Edge function needs to read it
  // - Secure: Both dev (HTTPS via mkcert) and prod use HTTPS
  // - SameSite=Lax: Protect against CSRF while allowing navigation
  document.cookie = `${ACCESS_TOKEN_COOKIE}=${accessToken}; Path=/; Secure; Max-Age=${maxAge}; SameSite=Lax`;
}

/**
 * Clear access token cookie
 */
export function clearAccessTokenCookie() {
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; Path=/; Secure; Max-Age=0`;
}

/**
 * Set dev-only cookie for bundle selection (not HttpOnly, readable by webpack dev server)
 * Only used in development - production uses the accessToken cookie with JWT verification
 */
export function setDevAuthCookie() {
  if (process.env.NODE_ENV === 'production') return;
  // Use Secure flag since dev now uses HTTPS via mkcert
  document.cookie = `${DEV_AUTH_COOKIE}=true; Path=/; Secure; Max-Age=604800; SameSite=Lax`;
}

/**
 * Clear dev auth cookie
 */
export function clearDevAuthCookie() {
  if (process.env.NODE_ENV === 'production') return;
  document.cookie = `${DEV_AUTH_COOKIE}=; Path=/; Secure; Max-Age=0`;
}

/**
 * Decode JWT token to extract payload (without verification)
 */
export function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Store access token and its expiration time
 * Also sets access token cookie for Lambda@Edge bundle routing
 */
export function storeAccessToken(accessToken) {
  if (!accessToken) {
    return;
  }

  localStorage.setItem('accessToken', accessToken);

  // Decode token to get expiration time
  const payload = decodeJWT(accessToken);
  if (payload && payload.exp) {
    // exp is in seconds since epoch, convert to milliseconds
    const expiresAt = payload.exp * 1000;
    localStorage.setItem('accessTokenExpiresAt', expiresAt.toString());
  }

  // Set cookie for Lambda@Edge bundle routing
  setAccessTokenCookie(accessToken);
}

/**
 * Get access token from localStorage
 */
export function getAccessToken() {
  return localStorage.getItem('accessToken');
}

/**
 * Clear all auth tokens
 */
export function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('accessTokenExpiresAt');
  localStorage.removeItem('isAuthenticated');
  sessionStorage.removeItem('userInfo');
  clearAccessTokenCookie();
  clearDevAuthCookie();
}

/**
 * Get token expiration time from localStorage
 */
export function getTokenExpiresAt() {
  const expiresAtStr = localStorage.getItem('accessTokenExpiresAt');
  if (!expiresAtStr) {
    return null;
  }
  return parseInt(expiresAtStr, 10);
}

/**
 * Check if token is expired or about to expire (within 1 minute)
 */
export function isTokenExpiredOrExpiringSoon() {
  const expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    return true;
  }

  const now = Date.now();
  const oneMinuteFromNow = now + 60 * 1000;

  return expiresAt <= oneMinuteFromNow;
}

/**
 * Refresh access token
 */
export async function refreshAccessToken() {
  try {
    const response = await fetch(`${API_GATEWAY_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      if (data.accessToken) {
        storeAccessToken(data.accessToken);
        console.log('Token refreshed successfully');
        return true;
      }
    } else {
      console.error('Token refresh failed');
      clearTokens();
      return false;
    }
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  }
  return false;
}

/**
 * Ensure we have a valid access token, refreshing if needed
 */
export async function ensureValidToken() {
  const token = getAccessToken();

  // If no token or token is expired/expiring, try to refresh
  // The refresh token is in an HttpOnly cookie, so we can refresh even without an access token
  if (!token || isTokenExpiredOrExpiringSoon()) {
    console.log('Token missing or expired, attempting refresh...');
    return await refreshAccessToken();
  }

  return true;
}

/**
 * Authenticated fetch wrapper with automatic token refresh
 */
export async function authenticatedFetch(url, options = {}) {
  const hasValidToken = await ensureValidToken();
  if (!hasValidToken) {
    throw new Error('No valid access token available');
  }

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...(options.headers || {}),
  };

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
}

